import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPencil,
  faPlus,
  faTrash,
  faChevronLeft,
  faChevronRight,
  faLayerGroup,
} from "@fortawesome/free-solid-svg-icons";
import { API_URL } from "../../config";
import { showToast } from "../../components/toast";
import LoadingSpinner from "../../components/LoadingSpinner";
import { PermissionWrapper } from "../../helpers/permission.js";

const SegmentManage = () => {
  const [segments, setSegments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(0); // 0 = Streetgains, 1 = Streetfolio
  const token = localStorage.getItem("authToken");

  // 🔎 Search Filters
  const [nameFilter, setNameFilter] = useState("");
  const [descFilter, setDescFilter] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  // 📑 Pagination
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [total, setTotal] = useState(0);
  const limit = 100;

  const fetchSegments = async () => {
    try {
      setLoading(true);
      const params = {
        type: activeTab,
        limit: 1000,
      };
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;

      const response = await axios.get(`${API_URL}segment`, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        params,
      });

      const allData = response.data?.data?.data || response.data?.data || [];
      setSegments(allData);
    } catch (error) {
      console.error("Error fetching segments:", error);
      showToast.error("Failed to fetch segments.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSegments();
  }, [activeTab, fromDate, toDate]);

  useEffect(() => {
    const handleClearFilters = () => {
      setNameFilter("");
      setDescFilter("");
      setFromDate("");
      setToDate("");
    };

    window.addEventListener("clearSegmentFiltersEvent", handleClearFilters);
    return () => {
      window.removeEventListener("clearSegmentFiltersEvent", handleClearFilters);
    };
  }, []);

  // 🔎 Filtered segments
  const filteredSegments = useMemo(() => {
    return segments.filter((item) => {
      if (
        nameFilter &&
        !(item.segment_name || "")
          .toLowerCase()
          .includes(nameFilter.toLowerCase())
      ) {
        return false;
      }

      if (
        descFilter &&
        !(item.segment_description || "")
          .toLowerCase()
          .includes(descFilter.toLowerCase())
      ) {
        return false;
      }

      if (item.createdon) {
        const itemDateStr = new Date(item.createdon).toISOString().split("T")[0];
        if (fromDate && itemDateStr < fromDate) {
          return false;
        }
        if (toDate && itemDateStr > toDate) {
          return false;
        }
      }

      return true;
    });
  }, [segments, nameFilter, descFilter, fromDate, toDate]);

  // 📑 Pagination calculations
  useEffect(() => {
    setTotal(filteredSegments.length);
    setPageCount(Math.ceil(filteredSegments.length / limit) || 1);
    setPage(1);
  }, [filteredSegments, limit]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pageCount) {
      setPage(newPage);
    }
  };

  const start = total === 0 ? 0 : (page - 1) * limit + 1;
  const end = Math.min(page * limit, total);
  const paginatedSegments = filteredSegments.slice((page - 1) * limit, end);

  const handleDelete = (id) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You will not be able to recover this segment!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const response = await axios.delete(`${API_URL}segment/${id}`, {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          if (response.data?.success || response.status === 200) {
            showToast.success("Segment deleted successfully.");
            fetchSegments();
          } else {
            showToast.error(response.data?.message || "Failed to delete segment.");
          }
        } catch (error) {
          console.error("Error deleting segment:", error);
          const errorMsg =
            error.response?.data?.message ||
            error.response?.data?.error ||
            "Failed to delete segment.";
          showToast.error(errorMsg);
        }
      }
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "-";
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;
      return date.toLocaleString("en-IN", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      }).replace(",", "");
    } catch {
      return dateString;
    }
  };

  return (
    <div
      id="page-container"
      className="page-header-fixed page-sidebar-fixed fade in"
    >
      <div id="content" className="">
        {/* Header section */}
        <div className="row pb-2">
          <div className="col-md-6">
            <h4 className="panel-title pull-left">
              <FontAwesomeIcon icon={faLayerGroup} className="me-2 text-primary" />{" "}
              Manage Segment
            </h4>
          </div>
          <div className="col-md-6" style={{ textAlign: "right" }}>
            <PermissionWrapper module="services" action="add">
              <Link
                to={`/research/segment/add?type=${activeTab}`}
                className="btn btn-default btn-sm me-2"
              >
                <FontAwesomeIcon icon={faPlus} /> Add Segment
              </Link>
            </PermissionWrapper>

            {/* Pagination Controls in Header */}
            <div className="d-inline-block ms-2">
              <span style={{ fontSize: "0.8rem" }} className="me-2">
                {start} to {end} of {total}
              </span>
              <button
                className="btn btn-default btn-sm"
                title="Previous"
                onClick={() => handlePageChange(page - 1)}
                disabled={page === 1}
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              &nbsp;
              <button
                className="btn btn-default btn-sm"
                title="Next"
                onClick={() => handlePageChange(page + 1)}
                disabled={page === pageCount || pageCount === 0}
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>
          </div>
        </div>

        {/* Tab Navigation - Merged Button Group */}
        <div className="m-b-15">
          <div className="btn-group" role="group" aria-label="Segment Type Selection">
            <button
              type="button"
              className={`btn btn-sm ${activeTab === 0 ? "btn-primary active" : "btn-default"}`}
              onClick={() => setActiveTab(0)}
              style={{
                padding: "8px 24px",
                fontWeight: "600",
                fontSize: "13px",
                backgroundColor: activeTab === 0 ? "#348fe2" : "#f1f3f5",
                color: activeTab === 0 ? "#ffffff" : "#495057",
                borderColor: activeTab === 0 ? "#348fe2" : "#ced4da",
                boxShadow: activeTab === 0 ? "0 2px 4px rgba(52, 143, 226, 0.3)" : "none",
                cursor: "pointer",
                zIndex: activeTab === 0 ? 2 : 1,
                borderTopLeftRadius: "4px",
                borderBottomLeftRadius: "4px",
                borderTopRightRadius: "0px",
                borderBottomRightRadius: "0px",
                transition: "all 0.2s ease-in-out",
              }}
            >
              Streetgains
            </button>
            <button
              type="button"
              className={`btn btn-sm ${activeTab === 1 ? "btn-primary active" : "btn-default"}`}
              onClick={() => setActiveTab(1)}
              style={{
                padding: "8px 24px",
                fontWeight: "600",
                fontSize: "13px",
                backgroundColor: activeTab === 1 ? "#00acac" : "#f1f3f5",
                color: activeTab === 1 ? "#ffffff" : "#495057",
                borderColor: activeTab === 1 ? "#00acac" : "#ced4da",
                boxShadow: activeTab === 1 ? "0 2px 4px rgba(0, 172, 172, 0.3)" : "none",
                cursor: "pointer",
                zIndex: activeTab === 1 ? 2 : 1,
                borderTopLeftRadius: "0px",
                borderBottomLeftRadius: "0px",
                borderTopRightRadius: "4px",
                borderBottomRightRadius: "4px",
                transition: "all 0.2s ease-in-out",
              }}
            >
              Streetfolio
            </button>
          </div>
        </div>

        {/* Panel with Table */}
        <div className="panel panel-default with-rounded-corner with-shadow">
          {loading ? (
            <LoadingSpinner />
          ) : (
            <div className="table-responsive">
              <table className="table table-condensed table-bordered bg-white m-b-0">
                <thead>
                  <tr style={{ background: "#f8f9fa" }}>
                    <th style={{ width: "50px", textAlign: "center" }}>#</th>
                    <th>Segment Name</th>
                    <th style={{ width: "90px", textAlign: "center" }}>Action</th>
                  </tr>
                  {/* Search Filter Row */}
                  <tr>
                    <th style={{ textAlign: "center" }}></th>
                    <th>
                      <div className="input-group input-group-sm">
                        <input
                          type="text"
                          className="form-control"
                          placeholder="Search segment..."
                          value={nameFilter}
                          onChange={(e) => setNameFilter(e.target.value)}
                        />
                      </div>
                    </th>
                    <th style={{ textAlign: "center" }}>
                      <button
                        className="btn btn-default btn-xs"
                        onClick={() => {
                          setNameFilter("");
                          setDescFilter("");
                          setFromDate("");
                          setToDate("");
                        }}
                        title="Clear filters"
                      >
                        Clear
                      </button>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedSegments.length > 0 ? (
                    paginatedSegments.map((item, index) => (
                      <tr key={item.id}>
                        <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                          {item.id}
                        </td>
                        <td style={{ verticalAlign: "middle", fontWeight: "600", color: "#333" }}>
                          {item.segment_name || "-"}
                        </td>
                        <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                          <div className="d-flex justify-content-center gap-1">
                            <PermissionWrapper module="services" action="edit">
                              <Link
                                to={`/research/segment/edit/${item.id}`}
                                className="btn btn-warning btn-xs me-1"
                                title="Edit Segment"
                              >
                                <FontAwesomeIcon icon={faPencil} />
                              </Link>
                            </PermissionWrapper>
                            <PermissionWrapper module="services" action="delete">
                              <button
                                type="button"
                                className="btn btn-danger btn-xs"
                                onClick={() => handleDelete(item.id)}
                                title="Delete Segment"
                              >
                                <FontAwesomeIcon icon={faTrash} />
                              </button>
                            </PermissionWrapper>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="3" style={{ textAlign: "center", padding: "30px", color: "#888" }}>
                        No segment records found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SegmentManage;
